// 游戏主渲染器

import type { LevelConfig } from '../types.js';
import { PuzzleGame } from '../core/PuzzleGame.js';
import { cellToPixel, drawHexPath, getPixelBoundingBox } from '../utils/hexUtils.js';

export interface GameLayout {
  // 手机外框（桌面端才有）
  phoneFrame: { x: number; y: number; width: number; height: number; radius: number } | null;
  // 左侧面板X坐标（桌面端）
  leftPanelX: number;
  // 手机游戏区域（内部Canvas的区域）
  gameArea: { x: number; y: number; width: number; height: number };
  // 拼图区域
  puzzleArea: { x: number; y: number; width: number; height: number };
  // 顶部栏高度
  topBarHeight: number;
  // 碎片区高度
  pieceAreaHeight: number;
  // 道具区高度
  itemAreaHeight: number;
  // 拼图区域的缩放比（从原图到屏幕）
  puzzleScale: number;
  // 图片在拼图区域的偏移
  puzzleOffset: { x: number; y: number };
}

export class GameRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private fullImage: HTMLImageElement | null = null;
  private layout: GameLayout;
  private config: LevelConfig;
  private pieceImages: Map<string, HTMLCanvasElement> = new Map();

  // 动画状态
  private unlockAnims: Map<string, { progress: number; duration: number }> = new Map();

  constructor(canvas: HTMLCanvasElement, config: LevelConfig, fullImage: HTMLImageElement | null, layout: GameLayout) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.config = config;
    this.fullImage = fullImage;
    this.layout = layout;
  }

  setFullImage(img: HTMLImageElement): void {
    this.fullImage = img;
  }

  setPieceImages(images: Map<string, HTMLCanvasElement>): void {
    this.pieceImages = images;
  }

  getPieceImage(pieceId: string): HTMLCanvasElement | undefined {
    return this.pieceImages.get(pieceId);
  }

  setLayout(layout: GameLayout): void {
    this.layout = layout;
  }

  getLayout(): GameLayout {
    return this.layout;
  }

  // 主渲染：绘制拼图区域
  drawPuzzleArea(game: PuzzleGame): void {
    const ctx = this.ctx;
    const { puzzleArea, puzzleOffset, puzzleScale } = this.layout;

    // 1. 画完整背景图 —— 只给图片本身加圆角，不影响蒙版和描边
    // ⚠️ 必须用 config.imageSize 而不是 fullImage.width！
    // 因为 puzzleScale 和 hex 坐标 (cellToPixel) 都是基于 config.imageSize 计算的，
    // 如果图片按实际尺寸画但 hex 按 config 尺寸算，两者就会错位！
    if (this.fullImage && this.config.imageSize) {
      const drawX = puzzleArea.x + puzzleOffset.x;
      const drawY = puzzleArea.y + puzzleOffset.y;
      const drawW = this.config.imageSize.width * puzzleScale;
      const drawH = this.config.imageSize.height * puzzleScale;

      // 单独 save/clip/restore 只影响这张图片
      ctx.save();
      this.arcRoundRectClip(ctx, drawX, drawY, drawW, drawH, 10);
      ctx.drawImage(this.fullImage, drawX, drawY, drawW, drawH);
      ctx.restore();
    } else {
      ctx.fillStyle = '#2a2a32';
      ctx.fillRect(puzzleArea.x, puzzleArea.y, puzzleArea.width, puzzleArea.height);
    }

    // 2. 为每个 cell 画纯色蒙版（hidden 中灰，opened 浅灰）
    const { cols, rows, hexSize, cropRegion } = this.config.grid as any;
    // cropRegion.left/top 就是像素值
    const cropLeft = cropRegion.left;
    const cropTop = cropRegion.top;

    // 2a. hidden 和 opened：纯色不透明蒙版盖底图
    for (const [key, cellInfo] of game.cells) {
      if (cellInfo.state === 'locked') continue;

      const [colStr, rowStr] = key.split(',');
      const col = parseInt(colStr);
      const row = parseInt(rowStr);

      const origPos = cellToPixel(col, row, hexSize, cropLeft, cropTop);
      const cx = puzzleArea.x + puzzleOffset.x + origPos.x * puzzleScale;
      const cy = puzzleArea.y + puzzleOffset.y + origPos.y * puzzleScale;
      const screenHexSize = hexSize * puzzleScale;

      drawHexPath(ctx, cx, cy, screenHexSize);

      if (cellInfo.state === 'hidden') {
        // 未开启：中灰色纯色（无渐变无透明，比之前浅）
        ctx.fillStyle = '#8a8a95';
        ctx.fill();
      } else if (cellInfo.state === 'opened') {
        // 已开启未放置：浅灰色纯色
        ctx.fillStyle = '#b5b5bd';
        ctx.fill();
      }
    }

    // 3. 画白色外轮廓描边（只画外轮廓：共用边自动消除）
    //    方法：收集所有 opened+locked 六边形的每条边，标记为 (cellKey, vStart, vEnd)
    //    两条相同顶点的边如果来自同状态 cell → 共用边，不画
    //    否则画出来
    type EdgeKey = string; // "x1,y1-x2,y2" （统一方向：较小顶点在前）
    const edges = new Map<EdgeKey, { count: number; sameState: boolean; state: string }>();

    const allCells: { key: string; state: string; vertices: { x: number; y: number }[] }[] = [];

    for (const [key, cellInfo] of game.cells) {
      if (cellInfo.state === 'hidden' || cellInfo.state === 'locked') continue;

      const [colStr, rowStr] = key.split(',');
      const col = parseInt(colStr);
      const row = parseInt(rowStr);

      const origPos = cellToPixel(col, row, hexSize, cropLeft, cropTop);
      const cx = puzzleArea.x + puzzleOffset.x + origPos.x * puzzleScale;
      const cy = puzzleArea.y + puzzleOffset.y + origPos.y * puzzleScale;
      const screenHexSize = hexSize * puzzleScale;

      const vertices = this.getHexVertices(cx, cy, screenHexSize);
      allCells.push({ key, state: cellInfo.state, vertices });

      for (let v = 0; v < 6; v++) {
        const a = vertices[v];
        const b = vertices[(v + 1) % 6];
        // 归一化：让较小的顶点在前（避免同一条边有两个方向）
        const keyEdge = (a.x < b.x || (a.x === b.x && a.y < b.y))
          ? `${a.x.toFixed(2)},${a.y.toFixed(2)}-${b.x.toFixed(2)},${b.y.toFixed(2)}`
          : `${b.x.toFixed(2)},${b.y.toFixed(2)}-${a.x.toFixed(2)},${a.y.toFixed(2)}`;

        const existing = edges.get(keyEdge);
        if (!existing) {
          edges.set(keyEdge, { count: 1, sameState: true, state: cellInfo.state });
        } else {
          existing.count++;
          existing.sameState = existing.sameState && existing.state === cellInfo.state;
        }
      }
    }

    // 只画 count 为奇数 OR count 为偶数但同状态为 false 的边
    // 实际上：count=1 → 外轮廓边 → 画
    //         count=2 且 sameState=true → 共用同状态边 → 不画
    //         count=2 且 sameState=false → 不同状态相邻边 → 画
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (const [ekey, info] of edges) {
      if (info.count === 2 && info.sameState) continue; // 同状态共用边，跳过
      // 画这条边
      const [p1, p2] = ekey.split('-');
      const [x1, y1] = p1.split(',').map(Number);
      const [x2, y2] = p2.split(',').map(Number);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }
  }

  // 获取六边形6个邻居的 key（平顶六边形方向顺序）
  private getHexNeighbors(col: number, row: number): string[] {
    // odd-q 平顶六边形邻居坐标偏移
    // 按顶点顺序：从右开始，顺时针
    const oddRow = row % 2 !== 0;
    if (oddRow) {
      return [
        `${col + 1},${row}`,      // 0: 右
        `${col},${row + 1}`,      // 1: 右下
        `${col - 1},${row + 1}`,  // 2: 左下
        `${col - 1},${row}`,      // 3: 左
        `${col - 1},${row - 1}`,  // 4: 左上
        `${col},${row - 1}`,      // 5: 右上
      ];
    } else {
      return [
        `${col + 1},${row}`,      // 0: 右
        `${col + 1},${row + 1}`,  // 1: 右下
        `${col},${row + 1}`,      // 2: 左下
        `${col - 1},${row}`,      // 3: 左
        `${col},${row - 1}`,      // 4: 左上
        `${col + 1},${row - 1}`,  // 5: 右上
      ];
    }
  }

  // 用 arcTo 构建圆角矩形并 clip（最可靠的方式）
  private arcRoundRectClip(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    if (w <= 0 || h <= 0) return;
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.arcTo(x + w, y, x + w, y + rr, rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
    ctx.lineTo(x + rr, y + h);
    ctx.arcTo(x, y + h, x, y + h - rr, rr);
    ctx.lineTo(x, y + rr);
    ctx.arcTo(x, y, x + rr, y, rr);
    ctx.closePath();
    ctx.clip();
  }

  // 获取平顶六边形的6个顶点坐标
  private getHexVertices(cx: number, cy: number, size: number): { x: number; y: number }[] {
    const vertices: { x: number; y: number }[] = [];
    for (let i = 0; i < 6; i++) {
      // 平顶六边形：第一个顶点在右侧（0°），每次 +60°
      const angle = Math.PI / 180 * (60 * i);
      vertices.push({
        x: cx + size * Math.cos(angle),
        y: cy + size * Math.sin(angle),
      });
    }
    return vertices;
  }

  // 绘制碎片区
  drawPieceArea(game: PuzzleGame, draggingPieceId: string | null = null, dragX: number = 0, dragY: number = 0): void {
    const ctx = this.ctx;
    const { gameArea, pieceAreaHeight, itemAreaHeight } = this.layout;
    // 碎片区域：道具区之上
    const pieceAreaY = gameArea.y + gameArea.height - itemAreaHeight - pieceAreaHeight;
    const pieceAreaX = gameArea.x;
    const pieceAreaWidth = gameArea.width;

    // 碎片区背景 - 渐变加深
    const grad = ctx.createLinearGradient(pieceAreaX, pieceAreaY, pieceAreaX, pieceAreaY + pieceAreaHeight);
    grad.addColorStop(0, 'rgba(0, 0, 0, 0.2)');
    grad.addColorStop(1, 'rgba(0, 0, 0, 0.55)');
    ctx.fillStyle = grad;
    ctx.fillRect(pieceAreaX, pieceAreaY, pieceAreaWidth, pieceAreaHeight);

    const slotWidth = pieceAreaWidth / 3;
    const padding = 10;

    for (let i = 0; i < game.displayPieces.length; i++) {
      const piece = game.displayPieces[i];
      if (!piece) continue;
      if (piece.id === draggingPieceId) continue;

      const pieceImg = this.pieceImages.get(piece.id);
      if (!pieceImg) continue;

      const slotX = pieceAreaX + i * slotWidth;

      // 计算缩放：确保碎片在slot内居中显示
      const scale = Math.min(
        (slotWidth - padding * 2) / pieceImg.width,
        (pieceAreaHeight - padding * 2) / pieceImg.height,
        1
      );
      const drawW = pieceImg.width * scale;
      const drawH = pieceImg.height * scale;
      const drawX = slotX + (slotWidth - drawW) / 2;
      const drawY = pieceAreaY + (pieceAreaHeight - drawH) / 2;

      // 金色发光效果
      ctx.save();
      ctx.shadowColor = '#FFD700';
      ctx.shadowBlur = 8;
      ctx.drawImage(pieceImg, drawX, drawY, drawW, drawH);
      ctx.restore();
    }
  }

  // 绘制拖拽中的碎片（带半透明预览）—— 中心对齐到传入坐标
  drawDraggingPiece(game: PuzzleGame, pieceId: string, centerX: number, centerY: number): void {
    const ctx = this.ctx;
    const piece = game.displayPieces.find(p => p.id === pieceId);
    if (!piece) return;

    const pieceImg = this.pieceImages.get(piece.id);
    if (!pieceImg) return;

    // 缩放和碎片区一致
    const { gameArea, pieceAreaHeight } = this.layout;
    const slotWidth = gameArea.width / 3;
    const padding = 10;
    const scale = Math.min(
      (slotWidth - padding * 2) / pieceImg.width,
      (pieceAreaHeight - padding * 2) / pieceImg.height,
      1
    );
    // 拖拽时放大1.1倍
    const dragScale = scale * 1.1;
    const drawW = pieceImg.width * dragScale;
    const drawH = pieceImg.height * dragScale;

    // 在拼图区域时半透明预览
    const { puzzleArea } = this.layout;
    const inPuzzleArea = centerX >= puzzleArea.x - drawW/2 && centerX <= puzzleArea.x + puzzleArea.width + drawW/2 &&
                         centerY >= puzzleArea.y - drawH/2 && centerY <= puzzleArea.y + puzzleArea.height + drawH/2;

    ctx.save();
    ctx.globalAlpha = inPuzzleArea ? 0.6 : 0.8;
    ctx.shadowColor = '#FFD700';
    ctx.shadowBlur = 15;
    // 以 centerX,centerY 为中心绘制
    ctx.drawImage(pieceImg, centerX - drawW / 2, centerY - drawH / 2, drawW, drawH);
    ctx.restore();
  }

  // 绘制查看道具的虚影
  drawHintGhosts(hintData: { pieceId: string; cells: { col: number; row: number }[] }[]): void {
    const ctx = this.ctx;
    const { puzzleArea, puzzleOffset, puzzleScale } = this.layout;
    const { hexSize, cropRegion } = this.config.grid as any;
    const cropLeft = cropRegion.left;
    const cropTop = cropRegion.top;

    const alphas = [0.3, 0.45, 0.55];
    // 和 PieceRenderer 里一致：padding = hexSize * 0.3
    const padding = hexSize * 0.3;

    hintData.forEach((data, idx) => {
      const pieceImg = this.pieceImages.get(data.pieceId);
      if (!pieceImg) return;

      const bbox = getPixelBoundingBox(data.cells, hexSize, cropLeft, cropTop);
      // ⚠️ piece canvas 左上角的 config 坐标是 bbox.minX - padding，不是 bbox.minX！
      const drawX = puzzleArea.x + puzzleOffset.x + (bbox.minX - padding) * puzzleScale;
      const drawY = puzzleArea.y + puzzleOffset.y + (bbox.minY - padding) * puzzleScale;
      const drawW = pieceImg.width * puzzleScale;
      const drawH = pieceImg.height * puzzleScale;

      ctx.save();
      ctx.globalAlpha = alphas[idx % alphas.length];
      ctx.drawImage(pieceImg, drawX, drawY, drawW, drawH);
      ctx.restore();
    });
  }

  // 更新动画
  updateAnims(deltaMs: number): void {
    for (const [key, anim] of this.unlockAnims) {
      anim.progress = Math.min(1, anim.progress + deltaMs / anim.duration);
      if (anim.progress >= 1) {
        this.unlockAnims.delete(key);
      }
    }
  }

  // 触发新解锁动画
  triggerUnlock(col: number, row: number): void {
    const key = `${col},${row}`;
    if (!this.unlockAnims.has(key)) {
      this.unlockAnims.set(key, { progress: 0, duration: 300 });
    }
  }

  // 根据屏幕坐标找到对应的六边形cell
  screenToCell(sx: number, sy: number): { col: number; row: number } | null {
    const { puzzleArea, puzzleOffset, puzzleScale } = this.layout;
    const { hexSize, cropRegion } = this.config.grid as any;
    const cropLeft = cropRegion.left;
    const cropTop = cropRegion.top;
    const { cols, rows } = this.config.grid;

    // 转到图片坐标
    const imgX = (sx - puzzleArea.x - puzzleOffset.x) / puzzleScale;
    const imgY = (sy - puzzleArea.y - puzzleOffset.y) / puzzleScale;

    // 遍历所有cells找最近的
    let bestCell: { col: number; row: number } | null = null;
    let bestDist = Infinity;

    for (let col = 0; col < cols; col++) {
      const rowCount = col % 2 === 0 ? rows : rows - 1;
      for (let row = 0; row < rowCount; row++) {
        const origPos = cellToPixel(col, row, hexSize, cropLeft, cropTop);
        const dx = imgX - origPos.x;
        const dy = imgY - origPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < bestDist && dist < hexSize * 1.2) {
          bestDist = dist;
          bestCell = { col, row };
        }
      }
    }

    return bestCell;
  }

  // 触发已锁定cells显示"放置正确"动画效果（在 GameApp 中调用）
}
